<script setup>
import { useI18n } from 'petite-vue-i18n'
import { computed, ref, watch } from 'vue'
import { useUserStore } from '../../../stores/user'
import { formatDateTime } from '../../../util/date'

const userStore = useUserStore()
const { t, locale } = useI18n()

const state = ref({
  visible: { type: Boolean, default: false },
  detailsVisible: { type: Boolean, default: false }
})

watch(
  () => userStore.user.signedIn,
  signedIn => {
    state.value.visible = signedIn && userStore.user.lastSession
    state.value.detailsVisible = false
  },
  { immediate: true }
)

const lastLoginMessage = computed(() => {
  const m = t('user_session.last_login', {
    date_time: formatDateTime(new Date(userStore.user.lastSession.created_at), locale),
    details: '{details}'
  })

  return m
})

const lastLoginMessageBeforeDetails = computed(() => {
  const index = lastLoginMessage.value.indexOf('{details}')
  return index === -1 ? lastLoginMessage.value : lastLoginMessage.value.substring(0, index)
})
const lastLoginMessageAfterDetails = computed(() => {
  const index = lastLoginMessage.value.indexOf('{details}')
  return index === -1 ? '' : lastLoginMessage.value.substring(index + '{details}'.length)
})

const detailsMessageParts = computed(() => {
  const detailsMessage = t('user_session.details', {
    ip_address: '{ip_address}',
    user_agent: '{user_agent}'
  })

  let parts = []
  for (const p of splitPlaceholder(
    detailsMessage,
    '{ip_address}',
    userStore.user.lastSession.ip_address
  )) {
    if (p.type === 'text') {
      parts = parts.concat(
        splitPlaceholder(p.content, '{user_agent}', userStore.user.lastSession.user_agent)
      )
    } else {
      parts.push(p)
    }
  }

  return parts
})

function splitPlaceholder(text, placeholderName, placeholderValue) {
  const index = text.indexOf(placeholderName)
  if (index === -1) {
    return text.length ? [{ type: 'text', content: text }] : []
  }

  return [].concat(
    index !== -1 ? [{ type: 'text', content: text.substring(0, index) }] : [],
    placeholderValue.length ? [{ type: 'placeholder', content: placeholderValue }] : [],
    index !== -1 && index + placeholderName.length < text.length
      ? [{ type: 'text', content: text.substring(index + placeholderName.length) }]
      : []
  )
}
</script>

<template>
  <div v-if="state.visible" class="app-user-session-toast">
    <div class="notification is-info is-light">
      <span class="ml-2" style="float: right;">
        <button type="button" class="delete" @click.prevent="state.visible = false" />
      </span>

      {{ lastLoginMessageBeforeDetails }}

      <button
        class="is-underlined"
        type="button"
        v-if="!state.detailsVisible"
        @click="state.detailsVisible = true"
      >
        {{ t('user_session.details_link') }}
      </button>
      <template v-if="state.detailsVisible">
        <span
          v-for="(part, i) of detailsMessageParts"
          :key="i"
          :class="{ 'is-italic': part.type !== 'text' }"
          >{{
            part.content
          }}</span
        >
      </template>

      {{ lastLoginMessageAfterDetails }}
    </div>
  </div>
</template>

<style scoped>
.app-user-session-toast {
  position: absolute;
  right: 1em;
  bottom: 1em;
}
.notification {
  background-color: var(--brand-yellow-light);
}
</style>
